import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from 'react-query'; // Added useQueryClient
import { KTIcon, KTSVG } from '../../../../_metronic/helpers'; // Assuming KTSVG is used correctly
import { change_active_status_user, delete_user, get_all_users_filter, get_filtered_users } from '../api/usersApi'; // Removed get_all_users for now
import { formatDynamicDateTime } from '../../../helper-functions/DateFunction';
import { useNavigate } from 'react-router-dom';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';

import { get_all_active_roles } from '../roles/api/rolesApi';
import { Form } from 'react-bootstrap';
import Flatpickr from 'react-flatpickr';
import { Roles } from '../../../modules/accounts/components/settings/SettingsModel';
import { getIsPeople } from '../../../helper-functions/general_settings_helper';
import { useGeneralSettings } from '../../../contexts/GeneralSettingsContext';

const isPeople = getIsPeople();
// Define User type based on API response for a single user
type User = {
  id: number;
  title: string | null; // API might return null
  picture: string | null;
  address: string | null;
  email: string | null;
  firstname: string | null;
  middlename: string | null;
  lastname: string | null;
  fullname?: string; // If API provides it directly, or we can construct it
  role: string | null; // Role name
  mobile: string | null;
  telephone: string | null;
  status: number; // 0 or 1
  department_name: string | null;
  created_at: string;
  updated_at: string;
  updated_by_name?: string | null; // Name of user who updated
  // Add any other fields from your 'usersArray' items
};

// Type for the pagination metadata from the API
type ApiPaginationInfo = {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
};

// Type for the 'data' object within the API response
type ApiUsersDataPayload = {
  users: User[]; // Array of user objects
  pagination: ApiPaginationInfo;
  links: {
    first_page_url: string | null;
    last_page_url: string | null;
    prev_page_url: string | null;
    next_page_url: string | null;
  };
};

// Type for the overall successful API response structure from get_filtered_users
type ApiUsersFullResponse = {
  data: { // This outer 'data' is from Axios
    success: boolean;
    message: string;
    data: ApiUsersDataPayload; // This inner 'data' is from your API structure
  };
};


var user_add: boolean, user_edit: boolean, user_view: boolean, user_logs: boolean, user_delete: boolean, calendar_create: boolean;

function UsersList() {
  const { getSettingValue } = useGeneralSettings();
  const userDateFormat = getSettingValue('date_format') || 'dd-mm-yyyy';
  const userTimeFormat = getSettingValue('time_format') || '12hr';
  const userTimeZone = getSettingValue('time_zone') || 'UTC';
  

  const [userList, setUserList] = useState<User[]>([]); // Will hold users for the current page
  // const [delayedLoading, setDelayedLoading] = useState(true); // This can be handled by react-query's isLoading/isFetching

  const [updatedByUserList, setUpdatedByUserList] = useState<User[]>([]); // For "Updated By" filter dropdown
  // const [departmentList, setDepartmentList] = useState<addDepartmentList[]>([]);
  const [roleList, setRoleList] = useState<Roles[]>([]);

  // Filter States
  const [selectedStatus, setSelectedStatus] = useState('');
  const [roleId, setRoleId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For client-side search on current page (optional)

  // State for server-side parameters
  const [filterPayloadForApi, setFilterPayloadForApi] = useState<any>({
    // Initial empty filters, page, and itemsPerPage
    fromDateFilter: "",
    toDateFilter: "",
    departmentId: "",
    roleId: "",
    selectedStatus: "",
    updatedBy: "",
    // Search term can also be sent to backend if backend supports it
    // searchTerm: "" 
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10); // Renamed from selectedItemsPerPage
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentRangeFrom, setCurrentRangeFrom] = useState<number>(1);
  const [currentRangeTo, setCurrentRangeTo] = useState<number>(0);


  // const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null); // Server-side sorting needed
  // Client-side sorting needs to be removed if using server-side pagination/sorting

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  // const [visibleData, setVisibleData] = useState<User[]>([]); // Not needed, userList will be the visible data

  // const [shouldFetch, setShouldFetch] = useState(false); // Let react-query handle fetching based on queryKey changes

  const fromDateRef = useRef<Flatpickr>(null);
  const toDateRef = useRef<Flatpickr>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // For invalidating queries

  user_add = permissionCheck('user-create');
  user_edit = permissionCheck('user-update');
  user_view = permissionCheck('user-view');
  user_logs = permissionCheck('user-activity-log');
  user_delete = permissionCheck('user-delete');
  calendar_create = permissionCheck('calendarsettinginfo-create');

  // Fetch data for "Updated By" dropdown (assuming get_all_active_users returns all users for this purpose)
  const { data: activeUsersForFilter } = useQuery('get_all_active_users_for_filter', get_all_users_filter, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data?.users) { // Adjust based on actual response of get_all_active_users
        setUpdatedByUserList(data.data.users);
      } else if (data?.data && Array.isArray(data.data)) { // If it's a direct array
        setUpdatedByUserList(data.data);
      }
    }
  });

  // Fetch roles for filter
  const { data: rolesData } = useQuery('get_all_active_roles_for_filter', get_all_active_roles, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data && Array.isArray(data.data)) {
        setRoleList(data.data as Roles[]);
      }
    }
  });


  const itemsPerPageOptions = [10, 20, 30, 50, 100];

  // --- Main Query for Filtered and Paginated Users ---
  const {
    data: filteredUsersQueryData, // Contains the full API response
    isLoading,
    isFetching, // Use isFetching for loading states during pagination/filter changes
    isError,
    error,
    // refetch, // Not needed to call manually if queryKey changes
  } = useQuery<ApiUsersFullResponse, Error>(
    // Query key now includes all parameters that trigger a refetch
    ['filtered_users', filterPayloadForApi, currentPage, itemsPerPage],
    () => get_filtered_users(
      filterPayloadForApi.selectedStatus || "",
      currentPage,
      itemsPerPage,
    ),
    {
      keepPreviousData: true, // Good for pagination UX
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const apiPayload: any = response.data.data; // Access the inner 'data' object
        if (apiPayload && apiPayload.data && Array.isArray(apiPayload.data)) {
          setUserList(apiPayload.data); // Set current page's users
          if (apiPayload.pagination) {
            setTotalItems(apiPayload.pagination.total);
            setTotalPages(apiPayload.pagination.last_page);
            setCurrentPage(apiPayload.pagination.current_page); // Sync with server's current page
            setCurrentRangeFrom(apiPayload.pagination.from);
            setCurrentRangeTo(apiPayload.pagination.to);
          }
        } else {
          setUserList([]);
          setTotalItems(0);
          setTotalPages(0);
          setCurrentRangeFrom(1);
          setCurrentRangeTo(0);
        }
      },
      onError: (error: any) => {
        toast.error(error.message || "Failed to fetch users.", {
          position: 'top-right', autoClose: 3000,
        });
        setUserList([]);
        setTotalItems(0);
        setTotalPages(0);
      },
    }
  );

  // Client-side search (optional, if you want to search only on the current page's data)
  // If you want server-side search, add searchInput to filterPayloadForApi and handle in backend
  const displayedUserList = searchInput
    ? userList.filter((user: User) =>
      (user.fullname?.toLowerCase() || `${user.firstname} ${user.lastname}`.toLowerCase()).includes(searchInput.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchInput.toLowerCase())
    )
    : userList;


  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to page 1 when filters change
    setFilterPayloadForApi({
      fromDateFilter,
      toDateFilter,
      departmentId,
      roleId,
      selectedStatus,
      updatedBy,
      // searchTerm: searchInput, // If you want to send search term to backend
    });
    // React Query will automatically refetch due to queryKey change
  };

  const handleClearFilters = () => {
    if (fromDateRef.current) fromDateRef.current.flatpickr.clear();
    if (toDateRef.current) toDateRef.current.flatpickr.clear();
    setFromDateFilter("");
    setToDateFilter("");
    setRoleId("");
    setDepartmentId(""); // Corrected from setdepartmentId
    setSelectedStatus("");
    setUpdatedBy("");
    setSearchInput(""); // Clear client-side search

    setCurrentPage(1); // Reset to page 1
    setFilterPayloadForApi({ // Reset all filter params for API
      fromDateFilter: "", toDateFilter: "", departmentId: "", roleId: "", selectedStatus: "", updatedBy: "", /* searchTerm: "" */
    });
  };

  // Mutations for delete and status change
  // IMPORTANT: After mutation, invalidate the query to refetch the current list
  const handleDeleteUser = useMutation(
    (id: number) => delete_user(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "User deleted successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_users', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete user.";
        toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
      },
    }
  );

  const handleStatusUpdate = useMutation(
    (id: number) => change_active_status_user(id),
    {
      onSuccess: (response) => {
        toast.success(response.message || "Status updated successfully.", { autoClose: 3000 });
        queryClient.invalidateQueries(['filtered_users', filterPayloadForApi, currentPage, itemsPerPage]);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || error.message || "Failed to update status.";
        toast.error(<div dangerouslySetInnerHTML={{ __html: errorMessage }} />, { autoClose: 6000 });
      },
    }
  );


  // Pagination handlers
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Go to first page when items per page changes
  };

  // Other handlers (handleUserLogs, handleUserEdit, handleUserView)
  const handleUserLogs = (user: User) => navigate("/users/user-logs", { state: { id: user.id } });
  const handleUserCalendar = (user: User) => navigate("/users/user-calendar-view", { state: { id: user.id, fullname: user.fullname } });
  const handleUserEdit = (user: User) => navigate("/users/edit-user", { state: { user: user } });
  const handleUserView = (user: User) => navigate("/users/view-user", { state: { user: user } });
  const handleUserWeeklyAvailibility = (user: User) => navigate("/users/weekly-availibility", { state: { user: user } });
  const handleUserHolidays = (user: User) => navigate("/holidays/list", { state: { user: user } });

  // Remove client-side sorting useEffect if sorting is handled by server
  // If server doesn't sort, you might keep client-side sorting on `userList` (current page data)
  // For now, removing handleSort and related useEffect for simplicity with server pagination

  if (isLoading && !isFetching && currentPage === 1) { // Initial load
    return <div className='d-flex justify-content-center align-items-center' style={{ height: '200px' }}><div className='spinner-border text-primary' role='status'><span className='visually-hidden'>Loading...</span></div></div>;
  }


  return (
    <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
      {/* Header: Add User Button */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Users List</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {totalItems > 0 ? `${totalItems-1} users found` : 'No users'}
          </span>
        </h3>
        <div className='card-toolbar'>
          {user_add && (
            <button className='btn btn-sm btn-light-dark' onClick={() => navigate('/users/add-user')}>
              <KTIcon iconName='plus' className='fs-3' /> Add User
            </button>
          )}
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className='card-body pt-0'>
        {/* Row 1: Search, From Date, To Date */}
        <div className='row mb-4 align-items-center'>
          <div className='col-md-3'>
            <div className='input-group'>
              <span className="input-group-text"><KTIcon iconName='magnifier' className='fs-3' /></span>
              <input
                type='text'
                className='form-control form-control-solid'
                placeholder='Search name, email, role...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              // If using server-side search, onBlur or a debounce would update filterPayloadForApi
              />
            </div>
          </div>
        </div>

        {/* Row 2: Role, Department, Updated By, Status */}
        <div className='row mb-4 align-items-center'>
          <div className='col-md-3'>
            <Form.Select value={selectedStatus} className='form-select form-select-solid' onChange={(e) => setSelectedStatus(e.target.value)}>
              <option value=''>Select Status...</option>
              <option value='1'>Active</option>
              <option value='0'>Inactive</option>
            </Form.Select>
          </div>
        </div>

        {/* Row 3: Filter Buttons */}
        <div className='row'>
          <div className='col-12 d-flex justify-content-end'>
            <button className='btn btn-light-danger me-3' onClick={handleClearFilters} disabled={isFetching}>
              <KTIcon iconName='cross' className='fs-4 me-1' /> Clear Filters
            </button>
            <button className='btn btn-primary' onClick={handleApplyFilters} disabled={isFetching}>
              <KTIcon iconName='filter-search' className='fs-4 me-1' /> Apply Filters
            </button>
          </div>
        </div>
      </div>


      {/* Table Section */}
      <div className='card-body py-3'>
        {isFetching && !isLoading && <div className='text-center text-muted p-3'><i>Updating user list...</i></div>}
        <div className='table-responsive'>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-50px rounded-start'>Pic</th>
                <th className='min-w-150px'>Full Name</th>
                <th className='min-w-120px'>Role</th>
                <th className='min-w-100px'>Status</th>
                <th className='min-w-150px'>Updated By</th>
                <th className='min-w-150px'>Updated At</th>
                {(user_delete || user_edit || user_logs || user_view) &&
                  <th className='min-w-150px text-center rounded-end'>Actions</th>
                }
              </tr>
            </thead>
            <tbody>
              {isLoading && currentPage === 1 ? ( // Show full table loader only on initial load
                <tr><td colSpan={8} className="text-center py-5"><div className='spinner-border text-primary' /></td></tr>
              ) : isError ? (
                <tr><td colSpan={8} className="text-center py-5 text-danger">Error loading users: {error?.message}</td></tr>
              ) : displayedUserList.length > 0 ? (
                displayedUserList.map((user) => (
                  <tr key={user.id}>
                    <td className='ps-4'>
                      <div className='symbol symbol-circle symbol-40px'>
                        {user.picture ? (
                          <img src={user.picture} alt={user.firstname || 'User'} />
                        ) : (
                          <div className="symbol-label fs-2 fw-bold text-inverse-primary bg-light-primary">
                            {(user.firstname || 'U')[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td><span className='text-dark fw-bold text-hover-primary d-block mb-1 fs-6'>{user.fullname || `${user.firstname || ''} ${user.middlename || ''} ${user.lastname || ''}`.trim()}</span><span className='text-muted fw-semibold text-muted d-block fs-7'>{user.email}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{user.role}</span></td>                    <td>
                      <span className={`badge ${user.status === 1 ? 'badge-light-success' : 'badge-light-danger'} fs-7 fw-bold`}>
                        {user.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{user.updated_by_name || 'N/A'}</span></td>
                    <td><span className='text-dark fw-semibold d-block fs-6'>{formatDynamicDateTime(user.updated_at, userDateFormat, userTimeFormat, userTimeZone)}</span></td>
                    {(user_delete || user_edit || user_logs || user_view) &&
                      <td className='text-center'>
                        <div className='d-flex justify-content-center flex-shrink-0'>
                          {(!!calendar_create || !!isPeople) && (
                            <button
                              onClick={() => handleUserCalendar(user)}
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              title="User Calendar View"
                            >
                              <i className="bi bi-calendar-event fs-4"></i>
                              
                            </button>
                          )}
                          {user_view && (
                            <button
                              onClick={() => handleUserWeeklyAvailibility(user)}
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              title="Set Working Hours"
                            >
                              <i className="bi bi-clock fs-4"></i>
                            </button>
                          )}
                          {user_view && (
                            <button
                              onClick={() => handleUserHolidays(user)}
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                              title="User Holidays"
                            >
                              <i className="bi bi-calendar-plus fs-4"></i>
                            </button>
                          )}
                          {user_view && (<button onClick={() => handleUserView(user)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="View User"><KTIcon iconName='eye' className='fs-3' /></button>)}
                          {user_edit && (<button onClick={() => handleUserEdit(user)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Edit User"><KTIcon iconName='pencil' className='fs-3' /></button>)}
                          {(user_add || user_edit) && (<button onClick={() => handleStatusUpdate.mutate(user.id)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="Toggle Status"><KTIcon iconName='switch' className='fs-3' /></button>)}
                          {user_logs && (<button onClick={() => handleUserLogs(user)} className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' title="User Logs"><KTIcon iconName='notepad-bookmark' className='fs-3' /></button>)}
                          {user_delete && (<button onClick={() => setSelectedUser(user)} className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title="Delete User" data-bs-toggle='modal' data-bs-target='#kt_modal_delete_user'><KTIcon iconName='trash' className='fs-3' /></button>)}
                        </div>
                      </td>
                    }
                  </tr>
                ))
              ) : (
                !isFetching && <tr><td colSpan={8} className="text-center py-5">No users found matching your criteria.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer: Pagination and Items Per Page */}
      {totalItems > 0 && (
        <div className='card-footer d-flex justify-content-between align-items-center py-4'>
          <div className='d-flex align-items-center'>
            <strong className='me-3'>Results Per Page:</strong>
            <select
              className='form-select form-select-sm form-select-solid'
              style={{ width: '80px' }}
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              disabled={isFetching}
            >
              {itemsPerPageOptions.map(option => (<option key={option} value={option}>{option}</option>))}
            </select>
          </div>

          <div className='d-flex align-items-center'>
            <span className='text-muted me-3'>
              Showing <strong>{currentRangeFrom}</strong> - <strong>{currentRangeTo > totalItems ? totalItems : currentRangeTo-1}</strong> of <strong>{totalItems-1}</strong> users
            </span>
            <div className='d-flex'>
              <button
                className='btn btn-light-primary btn-sm'
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || isFetching}
              >
                Previous
              </button>
              <button
                className='btn btn-primary btn-sm ms-2'
                onClick={handleNextPage}
                disabled={currentPage === totalPages || totalPages === 0 || isFetching}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <div className='modal fade' tabIndex={-1} id='kt_modal_delete_user'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Delete User Confirmation</h5>
              <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
            </div>
            <div className='modal-body'>
              <p>Are you sure you want to delete the user: <br />
                <strong>{selectedUser?.fullname || `${selectedUser?.firstname || ''} ${selectedUser?.lastname || ''}`}</strong>?
              </p>
              <p className='text-muted small'>This action cannot be undone.</p>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' data-bs-dismiss='modal'>Cancel</button>
              <button
                type='button'
                className='btn btn-danger'
                onClick={() => { if (selectedUser) handleDeleteUser.mutate(selectedUser.id); }}
                data-bs-dismiss='modal' // Dismiss modal after click
                disabled={handleDeleteUser.isLoading}
              >
                {handleDeleteUser.isLoading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersList;